import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, LinearProgress, Link, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { useSnackbar } from 'notistack'
import RequestDetailDrawer from '../components/RequestDetailDrawer'
import {
  approveRequest, extractPiiError, fetchInputFileUrl, fetchPendingApprovals, rejectRequest,
} from '../api'
import { formatDate1 } from '../../../common/util'

const REJECT_REMARK_MIN = 10

/**
 * Screen 3 — Pending Approvals (BRD §7.4). Server buckets rows to the caller's
 * grants; the Approval-level badge tells a dual-grant user which hat each row needs.
 * Own requests come flagged selfRequest=true and act disabled (server enforces too).
 */
export default function PendingApprovalsTab({ isL2 }) {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [detailId, setDetailId] = useState(null)
  const [action, setAction] = useState(null) // { type: 'approve'|'reject', row }
  const [remark, setRemark] = useState('')
  const [acting, setActing] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchPendingApprovals({ page: page + 1, size })
      .then((res) => {
        setRows(res.data?.items || [])
        setTotal(res.data?.totalRecords || 0)
        setError(null)
      })
      .catch((err) => setError(extractPiiError(err, 'Failed to load pending approvals')))
      .finally(() => setLoading(false))
  }, [page, size])

  useEffect(() => { load() }, [load])

  const viewAwbList = (r) => {
    fetchInputFileUrl(r.requestId)
      .then((res) => {
        const url = res.data?.downloadUrl
        if (url) window.open(url, '_blank', 'noopener')
      })
      .catch((err) => enqueueSnackbar(extractPiiError(err, 'Input file unavailable'), { variant: 'error' }))
  }

  const closeAction = () => { setAction(null); setRemark('') }

  const performAction = () => {
    const { type, row } = action
    const call = type === 'approve'
      ? approveRequest(row.requestId, row.approvalLevel, remark.trim() || undefined)
      : rejectRequest(row.requestId, row.approvalLevel, remark.trim())
    setActing(true)
    call
      .then(() => {
        enqueueSnackbar(
          type === 'approve' ? `Request ${row.requestId} approved` : `Request ${row.requestId} rejected`,
          { variant: 'success' },
        )
        closeAction()
        load()
      })
      .catch((err) => enqueueSnackbar(extractPiiError(err, `Failed to ${type}`), { variant: 'error' }))
      .finally(() => setActing(false))
  }

  const rejectValid = action?.type !== 'reject' || remark.trim().length >= REJECT_REMARK_MIN

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <IconButton onClick={load} title="Refresh"><RefreshOutlinedIcon /></IconButton>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Requested by</TableCell>
              <TableCell>Submitted on</TableCell>
              <TableCell align="right">AWB count</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Level</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const self = !!r.selfRequest
              return (
                <TableRow key={r.requestId} hover sx={self ? { opacity: 0.55 } : undefined}>
                  <TableCell>
                    <Button size="small" onClick={() => setDetailId(r.requestId)}>{r.requestId}</Button>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.requestedByName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.requestedByEmail}</Typography>
                  </TableCell>
                  <TableCell>{r.submittedOn ? formatDate1(r.submittedOn) : '—'}</TableCell>
                  <TableCell align="right">
                    <Stack alignItems="flex-end">
                      <span>{r.awbCount != null ? r.awbCount : '—'}</span>
                      <Link component="button" variant="caption" onClick={() => viewAwbList(r)}>View AWB list</Link>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Tooltip title={r.reason || ''}>
                      <Typography variant="body2" noWrap>{r.reason}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={r.approvalLevel || '—'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={self ? 'You cannot approve your own request' : ''}>
                      <span>
                        <IconButton
                          size="small" color="success" disabled={self}
                          onClick={() => setAction({ type: 'approve', row: r })}
                          title="Approve"
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small" color="error" disabled={self}
                          onClick={() => setAction({ type: 'reject', row: r })}
                          title="Reject"
                        >
                          <HighlightOffIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  Nothing awaiting your approval.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={size}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0) }}
      />

      <Dialog open={!!action} onClose={acting ? undefined : closeAction} fullWidth maxWidth="xs">
        <DialogTitle>
          {action?.type === 'approve' ? 'Approve request' : 'Reject request'} {action?.row?.requestId}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {action?.type === 'approve'
              ? `Approve request ${action?.row?.requestId} for ${action?.row?.awbCount ?? '—'} AWBs at level ${action?.row?.approvalLevel}? This action is audited.`
              : 'A rejection remark is mandatory and will be emailed to the requester.'}
          </DialogContentText>
          <TextField
            label={action?.type === 'approve' ? 'Remark (optional)' : `Remark (min ${REJECT_REMARK_MIN} characters)`}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            fullWidth multiline minRows={2} autoFocus
            error={action?.type === 'reject' && remark.length > 0 && !rejectValid}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={acting}>Cancel</Button>
          <Button
            variant="contained"
            color={action?.type === 'approve' ? 'success' : 'error'}
            disabled={acting || !rejectValid}
            onClick={performAction}
          >
            {action?.type === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <RequestDetailDrawer
        requestId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        isL2={isL2}
        onChanged={load}
      />
    </Box>
  )
}
